import React from "react";
import PaymentsAppLayout from "../../../../Components/Molecules/PaymentsAppLayout";
import PrivateComponentLoadingPage from "../../../LoadingPage/PrivateComponentLoadingPage";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { ReactComponent as QuickbooksLogo } from "../../../../Assets/SettingsPages/IntegrationPage/QuickbooksLogo.svg";
import { ReactComponent as CheckMarkIcon } from "../../../../Assets/ActionIcons/CheckMarkIcon.svg";
import { useIntegrationStore } from "../../../../Store/Integration";
import { AdyenPaymentMethodType, getPaymentMethodDetails } from "../../../../Contracts/Transaction";
import {
  PaymentsIntegrationsMethodMappingRequest,
  QuickbooksSettingsRequest
} from "../../../../Contracts/Integration";
import { useNavigate } from "react-router-dom";
import { PaymentRoutes } from "../../../../Contracts/Routes";

enum StepsEnum {
  Settings = "Settings",
  Mapping = "Mapping"
}

const QuickbooksSettingsPage: React.FC = () => {
  const [currentStep, setCurrentStep] = React.useState<StepsEnum>(StepsEnum.Settings);
  const navigate = useNavigate();
  const [quickbooksSettings, setQuickbooksSettings] = React.useState<QuickbooksSettingsRequest>({
    configuration: [],
    payment_method: []
  });
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [requiredFieldsMissingError, setRequiredFieldsMissingError] =
    React.useState<boolean>(false);
  const { getAccessTokenSilently } = useAuth0();
  const {
    getQuickbooksMetaData,
    quickbooksMetaDataView,
    getSavedQuickbooksMetaData,
    savedQuickbooksMappingView,
    createOrUpdateQuickbooksSettings
  } = useIntegrationStore();

  const refreshThings = React.useCallback(async () => {
    setIsLoading(true);
    await Promise.all([
      getQuickbooksMetaData(getAccessTokenSilently),
      getSavedQuickbooksMetaData(getAccessTokenSilently)
    ]);
    setIsLoading(false);
  }, [getQuickbooksMetaData, getSavedQuickbooksMetaData, getAccessTokenSilently]);

  React.useEffect(() => {
    refreshThings();
  }, [refreshThings]);

  React.useEffect(() => {
    setRequiredFieldsMissingError(false);
  }, [currentStep]);

  React.useEffect(() => {
    if (savedQuickbooksMappingView) {
      const newConfiguration = savedQuickbooksMappingView.configuration.map(config => ({
        zomentum_key: config.key,
        quickbook_key: config.value
      }));
      const newPaymentMethods: PaymentsIntegrationsMethodMappingRequest[] =
        savedQuickbooksMappingView.payment_method.map(method => ({
          payment_integration_ref: method.payment_integration_ref,
          partner_configured_payment_method:
            method.partner_configured_payment_method as AdyenPaymentMethodType
        }));
      setQuickbooksSettings(prevSettings => ({
        ...prevSettings,
        configuration: newConfiguration,
        payment_method: newPaymentMethods
      }));
    }
  }, [savedQuickbooksMappingView]);

  const onSettingsSave = async () => {
    if (validateRequiredFields()) {
      setRequiredFieldsMissingError(true);
      return;
    }
    setRequiredFieldsMissingError(false);
    setIsSaving(true);
    if (currentStep === StepsEnum.Mapping) {
      await createOrUpdateQuickbooksSettings(getAccessTokenSilently, quickbooksSettings);
    }
    setIsSaving(false);
    if (currentStep === StepsEnum.Settings) setCurrentStep(StepsEnum.Mapping);
    else {
      navigate(PaymentRoutes.SETTINGS_INTEGRATION);
    }
  };

  const mappingZomentumDisplayAttributes =
    quickbooksMetaDataView?.payment_methods.zomentum_payment_methods.map(method => {
      return {
        key: method.key,
        ...getPaymentMethodDetails(method.key as AdyenPaymentMethodType)
      };
    }) || [];

  const setQuickbooksConfiguration = (zomentum_key: string, quickbook_key: string) => {
    const existingConfigIndex = quickbooksSettings.configuration.findIndex(
      config => config.zomentum_key === zomentum_key
    );

    const newConfiguration =
      existingConfigIndex !== -1
        ? quickbooksSettings.configuration.map((config, index) =>
            index === existingConfigIndex ? { zomentum_key, quickbook_key } : config
          )
        : quickbooksSettings.configuration.concat({
            zomentum_key,
            quickbook_key
          });

    setQuickbooksSettings({
      ...quickbooksSettings,
      configuration: newConfiguration
    });
  };

  const setQuickbooksPaymentMethod = (
    payment_integration_ref: string,
    partner_configured_payment_method: string
  ) => {
    const existingPaymentMethodIndex = quickbooksSettings.payment_method.findIndex(
      method =>
        method.partner_configured_payment_method ===
        (partner_configured_payment_method as AdyenPaymentMethodType)
    );

    const newPaymentMethods: PaymentsIntegrationsMethodMappingRequest[] =
      existingPaymentMethodIndex !== -1
        ? quickbooksSettings.payment_method.map((method, index) =>
            index === existingPaymentMethodIndex
              ? {
                  payment_integration_ref: parseInt(payment_integration_ref),
                  partner_configured_payment_method:
                    partner_configured_payment_method as AdyenPaymentMethodType
                }
              : method
          )
        : quickbooksSettings.payment_method.concat({
            payment_integration_ref: parseInt(payment_integration_ref),
            partner_configured_payment_method:
              partner_configured_payment_method as AdyenPaymentMethodType
          });

    setQuickbooksSettings({
      ...quickbooksSettings,
      payment_method: newPaymentMethods
    });
  };

  const getConfigValue = (key: string | undefined) =>
    quickbooksSettings.configuration.find(config => config.zomentum_key === key)?.quickbook_key ||
    "";

  const vendorConfigKey = quickbooksMetaDataView?.vendors.zomentum_payment_key.key;
  const expenseConfigKey = quickbooksMetaDataView?.expense_accounts.zomentum_payment_key.key;
  const incomeConfigKey = quickbooksMetaDataView?.income_accounts.zomentum_payment_key.key;
  const payoutConfigKey = quickbooksMetaDataView?.payout_bank_accounts.zomentum_payment_key.key;

  const vendorConfigValue = getConfigValue(vendorConfigKey);
  const expenseConfigValue = getConfigValue(expenseConfigKey);
  const incomeConfigValue = getConfigValue(incomeConfigKey);
  const payoutConfigValue = getConfigValue(payoutConfigKey);

  const validateRequiredFields = (): boolean => {
    if (currentStep === StepsEnum.Settings) {
      const requiredFields = [
        vendorConfigValue,
        expenseConfigValue,
        incomeConfigValue,
        payoutConfigValue
      ];
      return requiredFields.some(field => field === "");
    } else {
      const requiredFields = mappingZomentumDisplayAttributes.map(
        zValue =>
          quickbooksSettings.payment_method.find(
            method => method.partner_configured_payment_method === zValue.key
          )?.payment_integration_ref
      );
      return requiredFields.some(field => field === undefined);
    }
  };

  return (
    <PaymentsAppLayout noPadding>
      <div className="grid md:grid-cols-5 grid-cols-1">
        <div className="col-span-1 border-r p-6 overflow-hidden">
          <QuickbooksLogo />
          <div>
            Connect QuickBooks Online with Zomentum Payments to sync your clients, auto-collect
            invoices and record payments and fees.
          </div>
        </div>
        <div className="md:col-span-4 col-span-1">
          <div className="flex border-t border-b md:border-t-0 md:border-b p-6 items-center space-x-2">
            <div className="flex align-center space-x-2">
              {currentStep === StepsEnum.Settings ? (
                <div className="bg-blue-500 text-white font-semibold rounded-full px-2">1</div>
              ) : (
                <CheckMarkIcon className="text-green-500 h-6 w-6 scale-125" />
              )}
              <div className={currentStep === StepsEnum.Settings ? "font-semibold" : ""}>
                Settings
              </div>
            </div>
            <div className="h-0.5 w-44 bg-blue-600 hidden md:block" />
            <div
              className={
                currentStep === StepsEnum.Settings
                  ? "border border-gray-400 text-gray-400 font-semibold rounded-full px-2"
                  : "bg-blue-500 text-white font-semibold rounded-full px-2"
              }
            >
              2
            </div>
            <div className={currentStep === StepsEnum.Settings ? "text-gray-400" : "font-semibold"}>
              Payment method mapping
            </div>
          </div>
          {currentStep === StepsEnum.Settings ? (
            <div className="md:h-[calc(100vh-16rem)] overflow-auto">
              <div className="p-6 border-b">
                <div className="flex flex-col space-y-2 max-w-2xl">
                  <div className="font-semibold">Vendor and Expense Account</div>
                  <div>
                    Zomentum Payments charges a processing fee for every transaction. We generate
                    expense transactions to reconcile these fees in order to balance your books.
                    These transactions are recorded in the Expense Account and linked to the Vendor
                    you choose here.
                  </div>
                  <label className="pt-2">
                    Select the Quickbooks Online Vendor to associate with Zomentum Payments
                    <sup className="text-red-600">*</sup>
                  </label>
                  {isLoading && <div className="w-full h-8 animate-pulse bg-gray-200" />}
                  {!isLoading && quickbooksMetaDataView?.vendors.quickbook_options && (
                    <select
                      value={vendorConfigValue}
                      className="border rounded px-2 py-2"
                      onChange={e =>
                        vendorConfigKey &&
                        setQuickbooksConfiguration(vendorConfigKey, e.target.value)
                      }
                    >
                      <option value="" disabled>
                        Select Vendor
                      </option>
                      {quickbooksMetaDataView.vendors.quickbook_options.map(vendor => (
                        <option key={vendor.key} value={vendor.key}>
                          {vendor.display_value}
                        </option>
                      ))}
                    </select>
                  )}
                  <label className="pt-2">
                    Select the Expense Account for fees charged by Zomentum Payments
                    <sup className="text-red-600">*</sup>
                  </label>
                  {isLoading && <div className="w-full h-8 animate-pulse bg-gray-200" />}
                  {!isLoading && quickbooksMetaDataView?.expense_accounts.quickbook_options && (
                    <select
                      value={expenseConfigValue}
                      className="border rounded px-2 py-2"
                      onChange={e =>
                        expenseConfigKey &&
                        setQuickbooksConfiguration(expenseConfigKey, e.target.value)
                      }
                    >
                      <option value="" disabled>
                        Select Expense Account
                      </option>
                      {quickbooksMetaDataView.expense_accounts.quickbook_options.map(ex => (
                        <option key={ex.key} value={ex.key}>
                          {ex.display_value}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="p-6 border-b">
                <div className="flex flex-col space-y-2 max-w-2xl">
                  <div className="font-semibold">Payout Bank Account</div>
                  <div>
                    Zomentum Payments will transfer your collected funds to the bank account you set
                    up for payouts during the Customer Identification process. This deposit will be
                    recorded automatically in the configured Bank Account in Quickbooks Online.
                  </div>
                  <label className="pt-2">
                    Select the Bank Account where the payout will be deposited to
                    <sup className="text-red-600">*</sup>
                  </label>
                  {isLoading && <div className="w-full h-8 animate-pulse bg-gray-200" />}
                  {!isLoading && quickbooksMetaDataView?.payout_bank_accounts.quickbook_options && (
                    <select
                      value={payoutConfigValue}
                      className="border rounded px-2 py-2"
                      onChange={e =>
                        payoutConfigKey &&
                        setQuickbooksConfiguration(payoutConfigKey, e.target.value)
                      }
                    >
                      <option value="" disabled>
                        Select Payout Account
                      </option>
                      {quickbooksMetaDataView.payout_bank_accounts.quickbook_options.map(pa => (
                        <option key={pa.key} value={pa.key}>
                          {pa.display_value}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="p-6">
                <div className="flex flex-col space-y-2 max-w-2xl">
                  <div className="font-semibold">Income Account</div>
                  <div>
                    If you collect surcharges on your payments, these surcharges are recorded as
                    income in your Quickbooks Online account when we pay out your collected funds.
                  </div>
                  <label className="pt-2">
                    Select Income Account to reconcile surcharges
                    <sup className="text-red-600">*</sup>
                  </label>
                  {isLoading && <div className="w-full h-8 animate-pulse bg-gray-200" />}
                  {!isLoading && quickbooksMetaDataView?.income_accounts.quickbook_options && (
                    <select
                      value={incomeConfigValue}
                      className="border rounded px-2 py-2"
                      onChange={e =>
                        incomeConfigKey &&
                        setQuickbooksConfiguration(incomeConfigKey, e.target.value)
                      }
                    >
                      <option value="" disabled>
                        Select Income Account
                      </option>
                      {quickbooksMetaDataView.income_accounts.quickbook_options.map(ia => (
                        <option key={ia.key} value={ia.key}>
                          {ia.display_value}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="md:h-[calc(100vh-16rem)] overflow-auto p-6 max-w-2xl">
              <div className="font-semibold">Payment Method Mapping</div>
              <div className="my-2">
                Effortlessly map your payment methods to QuickBooks Online integration.
              </div>
              <table className="w-full my-4">
                <thead>
                  <tr>
                    <th className="text-left bg-blue-700 text-white px-3 py-2 border-r border-r-white rounded-tl">
                      Zomentum
                    </th>
                    <th className="text-left bg-blue-700 text-white px-3 py-2 rounded-tr">
                      Quickbooks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {mappingZomentumDisplayAttributes.map(zValue => {
                    const quickbooksValue =
                      quickbooksSettings.payment_method
                        .find(method => method.partner_configured_payment_method === zValue.key)
                        ?.payment_integration_ref.toString() || "";

                    return (
                      <tr>
                        <td className="border px-3 py-2">
                          <div className="flex items-center space-x-2">
                            <img src={zValue.logo} alt={zValue.name} />
                            <div>{zValue.name}</div>
                          </div>
                        </td>
                        <td className="border px-3">
                          <select
                            value={`${quickbooksValue}`}
                            onChange={e => {
                              setQuickbooksPaymentMethod(e.target.value, zValue.key);
                            }}
                            className="py-2"
                          >
                            <option value="" disabled>
                              Select corresponding method in Quickbooks
                            </option>
                            {quickbooksMetaDataView?.payment_methods.quickbooks_payment_methods.map(
                              qValue => (
                                <option key={qValue.key} value={qValue.key}>
                                  {qValue.display_value}
                                </option>
                              )
                            )}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-6 border-t flex space-x-4 items-center">
            {currentStep === StepsEnum.Mapping && (
              <button
                className="px-6 py-1 border rounded hover:bg-gray-200"
                onClick={() => {
                  setCurrentStep(StepsEnum.Settings);
                }}
              >
                Back
              </button>
            )}
            <button
              className={`bg-blue-500 px-6 py-1 text-white rounded hover:bg-blue-400 ${
                isSaving && "opacity-50 cursor-progress"
              }`}
              onClick={onSettingsSave}
              disabled={isSaving}
            >
              {currentStep === StepsEnum.Settings ? "Next" : "Save"}
            </button>
            {requiredFieldsMissingError && (
              <div>
                {currentStep === StepsEnum.Settings ? (
                  <div className="text-red-600">
                    <sup className="mr-1">*</sup>Required fields are missing
                  </div>
                ) : (
                  <div className="text-red-600">
                    You must complete the mappings before proceeding
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(QuickbooksSettingsPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
